import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../services/authService';
import { useQuiz } from '../context/QuizContext';

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserInfo, flowStage, allowResults } = useQuiz();
  const [adminAllowed, setAdminAllowed] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!authService.isAuthenticated()) {
      // Redirect to login if not authenticated
      navigate('/');
      return;
    }

    // Ensure user info is in context
    const userInfo = authService.getUserInfo();
    if (userInfo.email) {
      setUserInfo(userInfo);
    }

    const p = location.pathname;
    const adminView = !!(location.state && location.state.adminView);
    
    if ((p || '').startsWith('/admin')) {
      setAdminAllowed(null);
      authService.getCurrentUser()
        .then((verifiedUser) => {
          if (cancelled) return;
          setUserInfo({ email: verifiedUser.email, name: verifiedUser.name });
          setAdminAllowed(!!verifiedUser.is_admin);
          if (!verifiedUser.is_admin) navigate('/forbidden');
        })
        .catch(() => {
          authService.logout();
          if (!cancelled) navigate('/');
        });
      return () => { cancelled = true; };
    }

    setAdminAllowed(null);
    authService.getCurrentUser()
      .then((verifiedUser) => {
        if (!cancelled) {
          setUserInfo({ email: verifiedUser.email, name: verifiedUser.name });
        }
      })
      .catch(() => {
        authService.logout();
        if (!cancelled) navigate('/');
      });

    if (adminView) {
      return;
    }

    // Block URL navigation away from quiz/microsoft-test while quiz is in progress
    if (flowStage === 'education_done' && p !== '/quiz') {
      navigate('/quiz');
      return;
    }

    if (flowStage === 'quiz_done' && p !== '/microsoft-test') {
      navigate('/microsoft-test');
      return;
    }

    if (p === '/quiz') {
      if (flowStage === 'final_done') {
        navigate('/results');
        return;
      }
      if (flowStage !== 'education_done') {
        navigate('/education');
        return;
      }
    }

    if (p === '/microsoft-test') {
      if (flowStage !== 'quiz_done' && flowStage !== 'final_done') {
        navigate('/quiz');
        return;
      }
    }

    if (p === '/results') {
      if (!allowResults && !adminView) {
        navigate('/microsoft-test');
        return;
      }
    }
    return () => { cancelled = true; };
  }, [navigate, setUserInfo, location, flowStage, allowResults]);
  
  useEffect(() => {
    const quizInProgress = flowStage === 'education_done' || flowStage === 'quiz_done';
    if (!quizInProgress || (location.pathname || '').startsWith('/admin')) {
      return;
    }
    const handler = () => {
      navigate(1);
    };
    window.addEventListener('popstate', handler);
    return () => {
      window.removeEventListener('popstate', handler);
    };
  }, [navigate, location.pathname, flowStage]);

  // Only render children if authenticated
  if (!authService.isAuthenticated()) {
    return null;
  }

  // Prevent flash of admin content for normal users
  if ((location.pathname || '').startsWith('/admin')) {
    if (adminAllowed !== true) return null;
  }

  return children;
};

export default ProtectedRoute;
